import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Activity,
  Calendar,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
} from "@/lib/icons";
import { codeLabel } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { optionalInt, optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { ProfileSection } from "./-components/chart/profile-section";
import { Summary } from "@/routes/_authenticated/_care/residents/$residentId/-components/stay/attendance-summary";

type Period = "month" | "year" | "lifetime";
type AttendanceSearch = { period?: "year" | "lifetime"; month?: number; year?: number };

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/attendance")({
  validateSearch: (search: Record<string, unknown>): AttendanceSearch => ({
    period: optionalOneOf(["year", "lifetime"] as const, search.period),
    month: optionalInt(search.month, 0, 11),
    year: optionalInt(search.year, 1990, 2200),
  }),
  component: ResidentAttendanceTab,
});

const dayKey = (value: Date) =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
/** A calendar day stored as a date; read without a timezone shift. */
const calendarDay = (value: string) => new Date(`${value.slice(0, 10)}T12:00:00`);

/** Days in the window, stopping at today so a future day is not counted. */
function daysElapsed(startDate: string, endDate: string, todayKey: string): number {
  const last = endDate < todayKey ? endDate : todayKey;
  if (last < startDate) return 0;
  return (
    Math.floor(
      (Date.parse(`${last}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86_400_000,
    ) + 1
  );
}
const daysBetween = (startDate: string, endDate: string) =>
  Math.floor(
    (Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86_400_000,
  ) + 1;

/** Present or away, for one resident. A day with no row is a present day, so the denominator is the
 * calendar and only absences are counted. */
function ResidentAttendanceTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const today = new Date();
  const todayKey = dayKey(today);
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<AttendanceSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const period: Period = params.period ?? "month";
  const month = params.month ?? today.getMonth();
  const year = params.year ?? today.getFullYear();
  const setPeriod = (next: Period) => patch({ period: next === "month" ? undefined : next });
  const setYear = (update: (value: number) => number) => {
    const next = update(year);
    patch({ year: next === today.getFullYear() ? undefined : next });
  };

  const admitted = resident.admissionDate ? dayKey(new Date(resident.admissionDate)) : "2020-01-01";
  const startDate =
    period === "month"
      ? dayKey(new Date(year, month, 1))
      : period === "year"
        ? `${year}-01-01`
        : admitted;
  const endDate =
    period === "month"
      ? dayKey(new Date(year, month + 1, 0))
      : period === "year"
        ? `${year}-12-31`
        : todayKey;
  const periodLabel =
    period === "month"
      ? format(new Date(year, month, 1), "MMMM yyyy")
      : period === "year"
        ? String(year)
        : "All time";

  const attendance = useQuery(
    orpc.care.residents.attendance.queryOptions({ input: { residentId, startDate, endDate } }),
  );
  const absences = (attendance.data ?? []).filter((row) => row.status !== "present");
  const past = absences.filter((row) => row.attendanceDate <= todayKey);
  const total = daysBetween(startDate, endDate);
  const elapsed = period === "lifetime" ? total : daysElapsed(startDate, endDate, todayKey);
  const present = Math.max(0, elapsed - past.length);
  const rate = elapsed > 0 ? Math.round((present / elapsed) * 100) : 100;
  const toDate = period !== "lifetime" && elapsed < total;

  const shiftMonth = (delta: number) => {
    const next = new Date(year, month + delta, 1);
    patch({
      month: next.getMonth() === today.getMonth() ? undefined : next.getMonth(),
      year: next.getFullYear() === today.getFullYear() ? undefined : next.getFullYear(),
    });
  };

  return (
    <ProfileSection
      title="Attendance tracking"
      description="Track and manage this resident's daily attendance"
      action={
        <Button variant="outline" size="sm" asChild>
          <Link to="/daily-roll">
            <ExternalLink className="size-4" />
            Open daily roll
          </Link>
        </Button>
      }
    >
      <div className="border-border-soft mb-6 flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <Select value={period} onValueChange={(value) => setPeriod(value as Period)}>
          <SelectTrigger className="w-32" aria-label="Period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="year">Year</SelectItem>
            <SelectItem value="lifetime">Lifetime</SelectItem>
          </SelectContent>
        </Select>
        {period === "lifetime" ? (
          <span className="text-muted-foreground font-medium">{periodLabel}</span>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label={period === "month" ? "Previous month" : "Previous year"}
              onClick={() => (period === "month" ? shiftMonth(-1) : setYear((value) => value - 1))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="min-w-[140px] text-center font-medium">{periodLabel}</span>
            <Button
              variant="outline"
              size="icon"
              aria-label={period === "month" ? "Next month" : "Next year"}
              onClick={() => (period === "month" ? shiftMonth(1) : setYear((value) => value + 1))}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        )}
      </div>

      {attendance.isPending ? (
        <Skeleton className="h-32" />
      ) : attendance.isError ? (
        <ReadFailed
          message="Could not load this resident's attendance."
          error={attendance.error}
          onRetry={() => void attendance.refetch()}
          retrying={attendance.isFetching}
        />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Summary
              icon={<Calendar className="text-primary size-6" />}
              label={periodLabel}
              value={`${present} / ${elapsed}`}
              caption={`Days present${toDate ? " (to date)" : ""}`}
            />
            <Summary
              icon={<CalendarOff className="text-destructive size-6" />}
              label={
                period === "lifetime" ? "Total" : period === "year" ? "This year" : "This month"
              }
              value={String(past.length)}
              caption={`Days absent${toDate ? " (to date)" : ""}`}
            />
            <Summary
              icon={<Activity className="text-ok size-6" />}
              label="Attendance rate"
              value={`${rate}%`}
              caption={period === "lifetime" ? "Overall" : toDate ? "To date" : "Complete"}
            />
          </div>
          <div>
            <h2 className="mb-4 text-lg font-semibold">Absence history: {periodLabel}</h2>
            {absences.length === 0 ? (
              <EmptyState
                title="No absences recorded"
                description={
                  period === "lifetime"
                    ? "This resident has no recorded absences."
                    : `This resident was present every day during ${periodLabel}.`
                }
              />
            ) : (
              <div className="space-y-3">
                {absences.map((row) => (
                  <Card key={row.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-destructive/10 mt-0.5 flex size-10 items-center justify-center rounded-md">
                        <CalendarOff className="text-destructive size-5" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {format(calendarDay(row.attendanceDate), "EEEE, MMMM d, yyyy")}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <Badge variant="secondary">
                            {codeLabel(row.absenceReason, "Unknown")}
                          </Badge>
                          {row.absenceLocation ? (
                            <span className="text-muted-foreground text-sm">
                              Location: {row.absenceLocation}
                            </span>
                          ) : null}
                        </div>
                        {row.absenceNotes ? (
                          <p className="text-muted-foreground mt-2 text-sm">{row.absenceNotes}</p>
                        ) : null}
                        {row.expectedReturnDate ? (
                          <p className="text-muted-foreground mt-1 text-sm">
                            Expected return:{" "}
                            {format(calendarDay(String(row.expectedReturnDate)), "MMMM d, yyyy")}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </ProfileSection>
  );
}
